// Thin feature wrappers over the generated `account` orval hooks/fetchers (doc 07 §4.3).
// Unwrapping happens via `selectData` per the envelope rule; list search is a POST driven through
// useServerTable.fetchPage using the plain fetcher (orval generated a mutation-style hook for it).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useServerTable } from '@/components/data-grid';
import { selectData, type PageResponse } from '@/api/envelope';
import {
  searchAdminAccounts,
  useViewAdminAccount,
  useCreateAdminAccount,
  getViewAdminAccountQueryKey,
  changeAdminStatus,
  updateAdminAccount,
  deleteAdminAccount,
  useListAdminRoles,
  useGetAdminActivityLog,
} from '@/api/generated/account/account';
import type { AccountViewResponseDTO } from './types';
import type { AccountStatusRequestDTO, AccountUpdatingRequestDTO } from '@/api/generated/model';

// Stable base key for the admin list; search/filters are appended internally by useServerTable.
export const ADMIN_LIST_KEY = ['admins'] as const;

const EMPTY_PAGE: PageResponse<AccountViewResponseDTO> = {
  rows: [],
  pageNumber: 1,
  totalPage: 1,
  totalElements: 0,
};

/** Server-paginated admin-account table (POST /admin/api/account/search). */
export function useAdminTable() {
  return useServerTable<AccountViewResponseDTO>({
    queryKey: ADMIN_LIST_KEY,
    // `search` -> AccountSearchRequestDTO.keyword; `pageParam` is the 1-based BE page number.
    fetchPage: async ({ pageParam, pageSize, search }) => {
      const trimmed = search.trim();
      // Omit `keyword` entirely when empty (exactOptionalPropertyTypes: no explicit `undefined`).
      const res = await searchAdminAccounts({
        pageNumber: pageParam,
        pageSize,
        ...(trimmed ? { keyword: trimmed } : {}),
      });
      // Generated envelope `data` may be undefined -> return an empty page (shared contract).
      return selectData(res) ?? EMPTY_PAGE;
    },
    mode: 'paged',
    urlKey: 'admins',
  });
}

/** Single admin-account detail (GET /admin/api/account/view-detail/{adminId}). */
export function useAdminDetail(adminId: number) {
  const query = useViewAdminAccount(adminId, {
    query: { enabled: Number.isFinite(adminId) },
  });
  // Unwrap the shape-A envelope -> AccountViewResponseDTO (guard undefined `data`).
  const data = query.data ? selectData(query.data) : undefined;
  return { ...query, data };
}

export { getViewAdminAccountQueryKey };

/**
 * Admin mutations, all wired to the generated `account` fetchers and invalidating the list (plus the
 * detail, when an adminId is known) on success — fixes the CMP defect where the list never refetched.
 *
 * UI no longer exposes create/edit/status/delete (TM-3368 — Entra owns admin lifecycle). Hooks remain
 * for any residual callers / future hardening; prefer not to rewire them into the portal UI.
 */
export function useAdminMutations(adminId?: number) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
    if (adminId != null) void qc.invalidateQueries({ queryKey: getViewAdminAccountQueryKey(adminId) });
  };

  const create = useCreateAdminAccount({ mutation: { onSuccess: invalidate } });

  const changeStatus = useMutation({
    mutationFn: (body: AccountStatusRequestDTO) => changeAdminStatus(body),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: (body: AccountUpdatingRequestDTO) => updateAdminAccount(body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteAdminAccount(id),
    onSuccess: invalidate,
  });

  return { create, changeStatus, update, remove };
}

const ACTIVITY_LOG_PAGE_SIZE = 50;

/** Assignable role catalog — GET /admin/api/account/roles (generated `useListAdminRoles`). */
export function useRoleCatalog() {
  const query = useListAdminRoles({
    query: { select: selectData, staleTime: 5 * 60 * 1000 },
  });
  return { ...query, data: query.data ?? [] };
}

/** Per-admin activity log — single capped page (newest first). */
export function useAdminActivityLog(adminId: number) {
  const query = useGetAdminActivityLog(
    adminId,
    { pageNumber: 1, pageSize: ACTIVITY_LOG_PAGE_SIZE },
    { query: { enabled: Number.isFinite(adminId), select: (res) => selectData(res)?.rows ?? [] } },
  );
  return { ...query, data: query.data ?? [] };
}
